import React, { useState, useEffect, createRef, useRef, useCallback, useContext } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';

import Wallet from '../../../Dashboard/Wallet';
import DisplayQRCode from '../../../../components/common/DisplayQRCode';
import SingleUpload from '../../../../components/Uploader';
import CheckinButton from '../../../../components/common/CheckinButton';
import { FileURLContext } from '../../../../contexts/FileURLContext';
import { authUserHasModuleAccessMany } from "../../../../utils/auth";

import Users from '../../../../api/Users';
import { AllSubscriptions, GroupDisplays, ContactInfo } from '../../../../components/common/UserInfo';

const WALLET_MODULE_ID = 216;
const SUBSCRIPTION_MODULE_ID = 75;
const GROUPS_MODULE_ID = 106;
const CHECKIN_SCANNER_MODULE_ID = 127; //If they have access to use the checkin scanner, they would have access to do so manually

export const CardInfo = (props) => {
    let history = useHistory();

    const profilePictureRef = createRef();
    const mountedRef = useRef(false);

    const imageURL = useContext(FileURLContext);
    const nopic = imageURL.noPic;

    const [ userInfo, setUserInfo ] = useState();
    const [ groups, setGroups ] = useState([]);
    const [userHasModulePermission, setUserHasModulePermission] = useState(null);

//#region useCallback

    const getUser=useCallback(async()=>{
        try{
            let response = await Users.get({id: props.user_id})
            if(!response.errors && mountedRef.current){
                setUserInfo(response.data[0])
            }
        }catch(ex){console.error(ex)}
    },[props.user_id]);

    const getGroups=useCallback(async()=>{
        try{
            let response = await Users.groups({id:props.user_id})
            if(!response.errors && mountedRef.current){
                let confirmed_groups = response.data.filter(user_group => user_group.group_member_status_id===2);
                setGroups(confirmed_groups);
            }
        }catch(ex){console.error(ex)}
    },[props.user_id]);

//#endregion useCallback

//#region useEffect

    useEffect(()=>{
        mountedRef.current = true

        return()=>{
            mountedRef.current=false
        }
    },[]);

    // check if auth user has permission to view this page for this user
    useEffect(() => {
        const checkPermission = async () => {
            try {
                let response = await authUserHasModuleAccessMany([
                    WALLET_MODULE_ID, 
                    SUBSCRIPTION_MODULE_ID, 
                    GROUPS_MODULE_ID,
                    CHECKIN_SCANNER_MODULE_ID
                ], props.user_id);
                setUserHasModulePermission(response);
            } catch (error) { console.error(error) }
        }
        checkPermission();
    }, [props.user_id]);

    useEffect(()=>{
        if(props.user && mountedRef.current) setUserInfo(props.user);
        else if(props && props.user_id && mountedRef.current) getUser();
    },[props, getUser, props.refresh]);

    useEffect(()=>{
        if(userInfo && mountedRef.current) getGroups();
    },[userInfo,getGroups])

//#endregion

    // updates the profile image after upload
    const uploadProfilePicHandler = async (formData) =>{
        if (formData){
            formData.append('user_id',props.user_id);
            formData.append('type',2);
            formData.append('description',"Profile Picture");
            await Users.upload(formData).then(res=>{
                if (!res.errors){
                    /*if (user?.profile?.profile_img_path){
                        user.profile.profile_img_path=data.file_url;
                    }*/
                }
            });
        }
    }

    if (!userInfo) return (
        <div className="text-center">
            <SkeletonTheme color="#e0e0e0">
                <Skeleton circle={true} height={100} width={100} style={{marginBottom:"1rem"}} />
                <Skeleton height={12} count={5} />
                <Skeleton height={12} style={{marginTop:"2rem"}} />
                <Skeleton height={12} count={5} />
            </SkeletonTheme>
        </div>
    );

    return (
        userInfo &&
        <React.Fragment>
        <Card className="user-profile-img-wallet-card mb-3">
            <div style={window.innerWidth <=1200 ? {width: "150px"} : {width: "275px"}}>
                <SingleUpload 
                    DzRef={profilePictureRef}
                    type="image/*, .heic, .heif"
                    previewHeight={window.innerWidth <=1200 ? "175px" : "250px"}
                    previewPadding="1rem"
                    backgroundSize="contain"
                    previewSrc={userInfo.profile_img_path || nopic}
                    onSend={uploadProfilePicHandler}
                >
                </SingleUpload>
                {/*<Image src={ userInfo.profile_img_path || nopic} roundedCircle className="profile-picture" />*/}
            </div>
            <div>
                <h4 className="text-center">
                    {userInfo.first_name} {userInfo.last_name}
                </h4>
                {userHasModulePermission && userHasModulePermission[CHECKIN_SCANNER_MODULE_ID] &&
                    <CheckinButton userInfo={userInfo} />
                }
            </div>
            <div>
                <Row>
                    {/* This is currently always 0, regardless of outstanding charges.  Can comment back in when it's used
                    <Col className="text-center">
                    <span className="bold">${userInfo?.wallet?.due_amount || 0.00}</span><br/>
                    Balance Due
                </Col>*/}
                    {props.upcoming_events &&                             
                        <Col>
                            <span className="bold">{props?.upcoming_events || "0"}</span><br/>
                            Upcoming Events
                        </Col>
                    }
                </Row>
            </div>
        </Card>
        <Card>
            <div className="profile-quick-glance-info">

                <div className="qr-card">
                    <Card.Subtitle>QR Code</Card.Subtitle>
                    <DisplayQRCode user_id={userInfo.id} />   
                </div>
                {userHasModulePermission && userHasModulePermission[WALLET_MODULE_ID] &&
                    <div>
                        {window.innerWidth <=1200 ? null : <hr/>}
                        <Card.Subtitle className="my-3">WALLET</Card.Subtitle>
                        <Wallet user={userInfo} compact={true} />
                    </div>
                }

                {userHasModulePermission && userHasModulePermission[SUBSCRIPTION_MODULE_ID] &&
                    <div>
                        {window.innerWidth <=1200 ? 
                            null 
                            : 
                            <>
                                <hr/>
                                {userInfo.subscriptions &&
                                    <AllSubscriptions 
                                        subscriptions={userInfo.subscriptions} 
                                        showDates={true} 
                                        showSingleNonActive={true}
                                        border={false}
                                        maxWidth="300px"
                                        col={true}
                                        maxCard="300px"
                                    />
                                }
                            </>
                        }

                    </div>
                }
                <div>
                    {window.innerWidth <=1200 ? null : <hr/>}
                    <ContactInfo 
                        user={userInfo} 
                        showLabels={false} 
                        border={false}
                        maxWidth="300px"
                    />
                </div>

                {userHasModulePermission && userHasModulePermission[GROUPS_MODULE_ID] &&
                    <div>
                    {window.innerWidth > 1200 && <hr/>}
                        <GroupDisplays 
                            userId={userInfo.id}
                            maxWidth="300px"
                            border={false}
                            onClick={(group)=>history.push({
                                    pathname: props?.currentUserRoleId > 6 ? `/p/groups/${group.group_id}` : `/p/my/groups/${group.group_id}`,
                                    //setting history states to make accurate breadcrumbs from whence a user came
                                    state: {
                                        fromPath: history.location,
                                        fromName: "Profile",
                                        fromUser: `${props?.user?.first_name} ${props?.user?.last_name}`
                                    }
                                })} 
                        />
                    </div>
                }

                
                {userInfo.social &&
                    <div>
                        <React.Fragment>
                            <hr/>
                            <Card.Subtitle className="my-3">SOCIAL PROFILES</Card.Subtitle>
                            <Card.Text>
                                {userInfo.social.map(social => {
                                    return (
                                        <Button key={`soc-${social.id}`} variant="light" size="sm" href={social.handle}>
                                            <i className={`fab fa-${social.name} color-${social.name}`}></i>
                                        </Button>
                                    );
                                })}
                            </Card.Text>
                        </React.Fragment>
                    </div>
                }
            </div>
            {userHasModulePermission && userHasModulePermission[SUBSCRIPTION_MODULE_ID] &&
                <div>
                    <br />
                    {window.innerWidth > 1200 ? 
                        null 
                        : 
                        <>
                            <hr/>
                            {userInfo.subscriptions &&
                                <AllSubscriptions 
                                    subscriptions={userInfo.subscriptions} 
                                    showDates={true} 
                                    showSingleNonActive={true}
                                    border={false}
                                    maxWidth="980px"
                                    col={false}
                                    maxCard="200px"
                                />
                            }
                        </>
                    }

                </div>
            }
        </Card>
        </React.Fragment>
    );
}